/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { getCurrentLanguageInfo } from "../../lib/i18n";
import { Modality } from "@google/genai";
import { adhdFunctionDeclarations } from "../../lib/adhd-function-declarations";

function AltairComponent() {
  const { setConfig, setModel } = useLiveAPIContext();
  const { i18n } = useTranslation();

  useEffect(() => {
    setModel("models/gemini-live-2.5-flash-preview");

    const currentLanguageInfo = getCurrentLanguageInfo();
    const languageInstruction =
      currentLanguageInfo.code !== "en"
        ? `\n\nIMPORTANT: The user has selected ${currentLanguageInfo.nativeName} as their preferred language. Please respond in ${currentLanguageInfo.nativeName} unless they specifically ask you to use English. Maintain your helpful and encouraging tone in their chosen language.`
        : "";

    const systemPrompt = `You are my ADHD assistant and helpful companion. You have these capabilities:

1. ADHD Task Management: Help create, organize, and prioritize todos and projects
2. Focus Coaching: Provide suggestions based on available time and task priorities  
3. Body Doubling: Offer companionship during focused work sessions

Key ADHD principles:
- Break large tasks into smaller, manageable pieces
- Match task suggestions to user's current capacity and context
- Provide gentle reminders without overwhelming
- Celebrate completed tasks for dopamine rewards
- Help with time estimation and priority setting
- Understand executive function challenges

DYNAMIC TASK MANAGEMENT: When creating todos, you should:

**TASK CREATION WORKFLOW:**
1. AUTOMATICALLY estimate all parameters without asking the user
2. FIRST call get_todos to analyze existing workload and patterns  
3. Based on existing tasks, intelligently estimate:
   - TIME: Look at similar tasks' estimated_minutes to gauge complexity
   - PRIORITY: Assess urgency vs importance based on task type and user's current workload
   - DUE DATE: Consider existing due dates and workload distribution to suggest realistic deadlines
4. Create the new task with create_todo
5. **CRITICAL: AFTER CREATING A NEW TASK, AUTOMATICALLY ANALYZE AND UPDATE ALL EXISTING TASKS**

**DYNAMIC TASK ANALYSIS (Execute after every new task creation):**
6. Call get_todos again to get the complete updated task list
7. Analyze how the new task affects the entire task ecosystem:
   - Shift priorities based on new urgency patterns
   - Adjust due dates to prevent overload periods
   - Rebalance workload distribution across days
   - Update time estimates based on new complexity patterns
8. Use update_multiple_todos to modify existing tasks that need adjustment
9. Provide a summary: "Created [new task] and rebalanced [X] existing tasks for optimal workflow"

**ANALYSIS FACTORS FOR REBALANCING:**
- Current overdue tasks (affects priority/urgency)
- Task density per day (redistribute to prevent overwhelm)
- Priority conflicts (ensure high-priority items don't cluster)
- Time availability gaps (move tasks to optimize schedule)
- Similar task patterns (standardize time estimates)
- Project deadlines (cascade priority adjustments)
- Energy and focus requirements (group similar tasks)

**SPECIFIC REBALANCING ACTIONS:**
- Lower priority of existing tasks when urgent ones are added
- Spread out high-cognitive-load tasks across different days
- Group similar tasks for batching efficiency
- Adjust due dates to create realistic daily workloads
- Update time estimates based on new task complexity patterns

When users mention tasks or projects, proactively suggest creating todos with automatic estimation and rebalancing.
When users seem overwhelmed, suggest filtering by priority or time available.
Always maintain this dynamic approach - the system should feel alive and continuously optimizing.`;

    const systemInstruction = systemPrompt + languageInstruction;

    setConfig({
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
      },
      systemInstruction: {
        parts: [
          {
            text: systemInstruction,
          },
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: adhdFunctionDeclarations },
      ],
    });
  }, [setConfig, setModel, i18n.language]);

  return null;
}

export const Altair = memo(AltairComponent);
